import React from 'react';
import { useLoading } from '../../contexts/LoadingContext';

interface DataLoadingIndicatorProps {
  className?: string;
}

const DataLoadingIndicator: React.FC<DataLoadingIndicatorProps> = ({ className = '' }) => {
  const { loading } = useLoading();

  if (!loading.isLoading) return null;

  const getProgressSteps = () => {
    switch (loading.type) {
      case 'mongodb':
        return [
          { label: 'Conectando ao MongoDB', completed: true },
          { label: 'Carregando empresas', completed: loading.message.includes('empresa') },
          { label: 'Carregando fluxos', completed: loading.message.includes('fluxo') },
          { label: 'Carregando colaboradores', completed: loading.message.includes('colaborador') },
          { label: 'Carregando configurações', completed: loading.message.includes('configuração') || loading.message.includes('Finalizando') },
        ];
      case 'empresas':
        return [
          { label: 'Conectando ao servidor', completed: true },
          { label: 'Buscando empresas', completed: loading.message.includes('empresa') },
        ];
      case 'fluxos':
        return [
          { label: 'Conectando ao servidor', completed: true },
          { label: 'Carregando fluxos', completed: true },
        ];
      default:
        return [
          { label: loading.message || 'Carregando', completed: true },
        ];
    }
  };

  const steps = getProgressSteps();
  const completedSteps = steps.filter(step => step.completed).length;
  const progress = (completedSteps / steps.length) * 100;

  const activeDot = Math.min(2, Math.floor(progress / 34));

  return (
    <div className={`fixed bottom-4 right-4 z-50 ${className}`}>
      <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl w-80 overflow-hidden">
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Spinner */}
            <div className="relative w-6 h-6 mt-0.5">
              <div className="absolute inset-0 rounded-full border-2 border-gray-200"></div>
              <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-black animate-spin"></div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">{loading.message}</div>
              {loading.submessage && (
                <div className="text-xs text-gray-600 truncate">{loading.submessage}</div>
              )}
            </div>
          </div>

          {/* Progress */}
          <div className="mt-3">
            <div className="h-1.5 bg-black/90 rounded-full overflow-hidden">
              <div className="h-full bg-gray-300 transition-all duration-500" style={{ width: `${progress}%` }}></div>
            </div>
          </div>

          {/* Steps list */}
          <div className="mt-3 space-y-1">
            {steps.map((step, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className={`w-1.5 h-1.5 rounded-full ${step.completed ? 'bg-black' : 'bg-gray-300'}`}></span>
                <span className={`truncate ${step.completed ? 'text-gray-800' : 'text-gray-500'}`}>{step.label}</span>
              </div>
            ))}
          </div>

          {/* Dots */}
          <div className="mt-3 flex items-center justify-center gap-1">
            {[0,1,2].map(i => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === activeDot ? 'bg-black' : 'bg-gray-400'}`}></span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataLoadingIndicator;
